import type {
  FieldPolicy,
  TypePolicies,
} from '@apollo/client/cache/inmemory/policies';

import type {
  FinancialTransactionsSpendingByMonth,
  GetTransactionsResponse,
} from '@generated-gql/generated-types';

const typePolicies: TypePolicies = {
  FinancialTransaction: {
    fields: {
      date: {
        read(date) {
          // Remove the timezone from `date`:
          // e.g. "2024-12-02T00:00:00Z" -> "2024-12-02"
          //
          // The date is always "UTC midnight", which we should not use
          // when displaying to the user.
          return date?.split('T')?.[0];
        },
      },
    },
  },
  Query: {
    fields: {
      /**
       * When paginating transactions,
       * append results to the end of the cached list
       */
      financialTransactions: {
        keyArgs: ['filter'],
        merge(existing, incoming, options) {
          const offset = options.args?.pagination?.offset ?? 0;

          const existingTransactions = existing?.transactions ?? [];
          const incomingTransactions = incoming?.transactions ?? [];

          // Use slice() to duplicate the array (since `existing` is immutable)
          const mergedTransactions = existingTransactions.slice(0);

          incomingTransactions.forEach((transaction, i) => {
            mergedTransactions[offset + i] = transaction;
          });

          return {
            ...incoming,
            transactions: mergedTransactions,
          };
        },
      } as FieldPolicy<GetTransactionsResponse>,

      /**
       * When fetching multiple results of financialTransactionsSpendingByMonth,
       * remove duplicates & append results to the end of the cached list
       */
      financialTransactionsSpendingByMonth: {
        keyArgs: ['filter'],
        merge(existing, incoming) {
          const existingResults = existing?.spendingByMonth ?? [];
          const incomingResults = incoming?.spendingByMonth ?? [];

          // Use slice() to duplicate the array (since `existing` is immutable)
          const mergedResults = existingResults.slice(0);

          incomingResults.forEach((incomingResult) => {
            if (
              existingResults.some(
                (result) => result.date === incomingResult.date,
              )
            ) {
              return;
            }

            mergedResults.push(incomingResult);
          });

          return {
            ...incoming,
            spendingByMonth: mergedResults,
          };
        },
      } as FieldPolicy<FinancialTransactionsSpendingByMonth>,
    },
  },
};

export default typePolicies;
